import * as zod from 'zod'
import type { ActionFunctionArgs } from 'react-router'
import type { AxiosInstance } from 'axios'
import type { FieldErrors, Resolver } from 'react-hook-form'
import type { Store } from 'redux'
import { isAxiosError } from 'axios'
import { parseFormData, validateFormData } from 'remix-hook-form'
import { redirectDocument } from 'react-router'
import { useSelector } from 'react-redux'
import { zodResolver } from '@hookform/resolvers/zod'

import { DocumentMeta } from '../components/DocumentMeta'
import { formServerError } from '../utils/formServerError'
import { useTranslate } from '../utils/translate'
import Theme from '../components/Theme'

const schema = zod.object({
  firstName: zod
    .string()
    .trim()
    .min(1, {
      error: 'firstNameInputField.validationMessages.isRequired',
    })
    .max(320),
  lastName: zod
    .string()
    .trim()
    .min(1, {
      error: 'lastNameInputField.validationMessages.isRequired',
    })
    .max(320),
  email: zod
    .string()
    .trim()
    .min(1, { error: 'emailInputField.validationMessages.isRequired' })
    .max(320)
    .pipe(zod.email({ error: 'emailInputField.validationMessages.invalidFormat' })),
  orderNumber: zod
    .string()
    .trim()
    .min(1, {
      error: 'orderNumberInputField.validationMessages.isRequired',
    })
    // BYD cockpit allows up to 40 chars for order numbers (max 20 for prefix and max 20 for number)
    .max(40),
  withdrawalScope: zod.enum(['completeOrder', 'individualItems']),
  items: zod
    .array(
      zod.object({
        name: zod
          .string()
          .trim()
          .min(1, { error: 'individualItemsSection.productNameInputField.validationMessages.isRequired' })
          .max(320),
        quantity: zod
          .number({
            error: 'individualItemsSection.quantityInputField.validationMessages.isRequired',
          })
          .min(1, {
            error: 'individualItemsSection.quantityInputField.validationMessages.minValue',
          }),
      }),
    )
    .optional(),
  contractWithdrawal: zod.literal('on', {
    error: 'contractWithdrawalCheckbox.errorMessage',
  }),
  _tac: zod.string().optional(),
  _csrf: zod.string().optional(),
})

const loader = ((api: AxiosInstance, store: Store) => async () => {
  const state = store.getState()
  const isBeyond = Boolean(state.getIn(['shop', 'beyond']))
  const isOrderWithdrawalEnabled = Boolean(state.getIn(['shop', 'userSettings', 'orderWithdrawal', 'active']))

  if (!isBeyond || !isOrderWithdrawalEnabled) {
    throw new Response('Not Found', { status: 404 })
  }

  const { data } = await api.get<{ csrfToken: string }>('/api/v2/orderwithdrawal')
  return data
}) satisfies View.LoaderFn

export type OrderWithdrawalLoaderData = View.LoaderData<typeof loader>
export type OrderWithdrawalFormData = zod.infer<typeof schema>
export const resolver = zodResolver(schema) as Resolver<OrderWithdrawalFormData>

export type OrderWithdrawalActionData = {
  errors?: FieldErrors<OrderWithdrawalFormData>
  defaultValues: Partial<Omit<OrderWithdrawalFormData, '_tac' | '_csrf'>>
  serverError?: boolean
  orderNotFound?: boolean
  requestId?: string | null
}

const action: View.ActionFn = ({ api, store }) =>
  async function ({ request }: ActionFunctionArgs) {
    // Remix Hook Form JSON-encodes values client-side; native form posts (no JS) don't.
    // Skip parseFormData's JSON.parse on the no-JS path so e.g. all-digit order numbers stay strings.
    const skipJsonParse = typeof window === 'undefined'
    const formData = await parseFormData<OrderWithdrawalFormData>(request, skipJsonParse)

    // Don't submit if the honeypot field is filled.
    if (String(formData._tac ?? '')) return

    const { errors } = await validateFormData(formData, resolver)

    const { _tac, _csrf, ...payload } = formData

    if (errors) {
      return Response.json({ errors, defaultValues: payload } satisfies OrderWithdrawalActionData, { status: 422 })
    }

    try {
      const state = store.getState()
      const locale = state.getIn(['shop', 'locale'])
      const shop = state.getIn(['shop', 'slug'])

      await api.post(
        '/api/v2/orderwithdrawal',
        { ...payload, _csrf },
        {
          params: {
            ...(shop ? { shop } : {}),
            ...(locale ? { locale } : {}),
          },
        },
      )
    } catch (err) {
      // The backend sends `code: 'orderNotFound'` for an unmatched order; show a specific
      // message for that. Match on the code, not just the 404 status — other failures
      // (including incidental 404s) must fall through to the general server error.
      if (isAxiosError(err) && err.response?.data?.code === 'orderNotFound') {
        return Response.json({ orderNotFound: true, defaultValues: payload } satisfies OrderWithdrawalActionData, {
          status: 404,
        })
      }
      return formServerError(err, payload)
    }

    return redirectDocument(`${new URL(request.url).pathname.replace(/\/$/, '')}/confirmation`)
  }

OrderWithdrawal.action = action
OrderWithdrawal.loader = loader

export default function OrderWithdrawal() {
  const t = useTranslate('shop', 'components.orderWithdrawalForm')
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))

  return (
    <>
      <DocumentMeta
        title={`${t('title')} - ${shop.get('title')}`}
        meta={[{ name: 'robots', content: 'noindex, follow' }]}
      />
      <Theme withLayout currentView="order-withdrawal">
        {(renderView, props) => renderView('OrderWithdrawal', props)}
      </Theme>
    </>
  )
}

OrderWithdrawal.contentCreationDisabled = () => true
